<template>
    <div class="mail-doc-canvas">
        <DocToolbar
            v-if="loaded && !preview"
            @command="onCommand"
        />
        <iframe
            ref="frame"
            sandbox="allow-same-origin"
            class="mail-doc-canvas__frame"
            :title="preview ? '메일 본문 미리보기' : '메일 본문 편집 캔버스'"
        />
    </div>
</template>

<script>
// same-origin iframe 기반 본문 WYSIWYG 캔버스.
// 로드/렌더/높이/경계감지(M1) + 편집·경계가드·직렬화(M2) + 변수 알약(M3) +
// 툴바·붙여넣기 정제·버튼 스타일 인라인화(M4).
//
// ⛔ write-once: iframe 문서는 마운트 시 딱 1회 write 한다. value 가 바뀌면(로케일 전환 등)
//    부모가 :key 로 이 컴포넌트를 재생성해야 한다. 재-write 는 포커스/selection 을 잃는다.
// ⛔ CommonJS 헬퍼는 이 코드베이스 관례(SupplementReview.vue)대로 default import 로 받는다.
import mailDoc from '../mailDoc'
import mailSanitize from '../mailSanitize'
import splitSync from '../htmlSplitSync'
import createDOMPurify from 'dompurify'
import DocToolbar from './DocToolbar.vue'

export default {
    name: 'MailDocCanvas',
    components: { DocToolbar },
    props: {
        // flat full HTML (flatOf 결과). 마운트 시점에 준비돼 있어야 한다.
        value: { type: String, default: '' },
        // 미리보기에서는 툴바/편집 이벤트를 연결하지 않고 value 변경 시 재렌더한다.
        preview: { type: Boolean, default: false },
        // 변수 알약에 표시할 실제 값 맵({ campaign_name: '…' }). null 이면 변수명 표시(기존 동작).
        values: { type: Object, default: null },
    },
    watch: {
        value(nextValue) {
            if (this.preview) this.reloadPreview(nextValue)
        },
        // 값이 바뀌면 문서 재작성 없이 알약 글자만 갱신(write-once 계약 유지).
        values(next) {
            if (this.doc) mailDoc.refreshChipValues(this.doc, next)
        },
    },
    data() {
        return {
            doctype: '',
            boundary: 'whole', // 'whole' | 'framed' | 'unknown-framed'
            loaded: false,
            resizeObserver: null,
            doc: null, // iframe.contentDocument
            editRoot: null, // 편집 가능 영역
            clamping: false, // selection clamp 재귀 방지
            listeners: [], // [{ target, type, fn }] 정리용
            heightTimers: [],
            heightFrames: [],
            originalFlat: '', // 로드 시점 원본(토큰 손실 비교용)
            purifier: null, // DOMPurify 인스턴스(붙여넣기 정제용)
        }
    },
    created() {
        // 입력 250ms 디바운스 emit. blur/emitChange 가 대기 타이머를 반드시 취소한다(경합 방지).
        this.emitter = splitSync.createDebouncedEmit(() => this.emitChange(), 250)
    },
    mounted() {
        this.loadIntoFrame(this.value)
    },
    beforeDestroy() {
        this.teardown()
    },
    methods: {
        loadIntoFrame(flat) {
            const iframe = this.$refs.frame
            if (!iframe) return

            // 편집 안전 판정 — unknown-framed(프레임인데 content-wrap 없음)는 콘텐츠 경계
            // 불명이라 WYSIWYG 위험. 부모가 source 모드로 폴백하도록 알린다.
            this.boundary = mailDoc.detectContentBoundary(flat)
            if (!this.preview && !mailDoc.isWysiwygEditable(flat)) {
                this.$emit('unsupported', { boundary: this.boundary })
                return
            }

            // outerHTML 은 DOCTYPE 를 잃으므로 로드 시점 원문을 보관 → 저장 시 재부착.
            this.doctype = mailDoc.captureDoctype(flat)

            // ★ write-once
            const doc = iframe.contentDocument
            doc.open()
            doc.write(flat)
            doc.close()

            this.doc = doc
            this.injectCanvasStyle(doc)
            this.bindAutoHeight(iframe, doc)

            if (this.preview) {
                this.enablePreview(doc)
            } else {
                this.enableEditing(doc)
            }

            this.loaded = true
            this.$emit('ready', { boundary: this.boundary, doctype: this.doctype })
        },

        // preview 모드는 같은 iframe 요소를 유지한 채 문서만 다시 쓴다.
        // 편집 모드의 write-once 계약은 그대로 유지된다.
        reloadPreview(flat) {
            if (!this.$refs.frame) return

            this.teardown()
            this.loaded = false
            this.doc = null
            this.editRoot = null
            this.clamping = false
            this.originalFlat = ''
            this.purifier = null

            this.loadIntoFrame(flat)
        },

        // 읽기 전용 미리보기: 편집 경계/리스너는 적용하지 않고 변수만 같은 칩으로 표시.
        // unknown-framed는 selectPreviewRoot가 body 전체로 폴백한다.
        enablePreview(doc) {
            const previewRoot = mailDoc.selectPreviewRoot(doc, this.boundary)
            if (previewRoot) mailDoc.flatToChips(previewRoot, doc, this.values)
        },

        // 편집 활성화 + 경계 가드(편집 영역 밖 선택·삭제 차단) + 입력 시 저장용 HTML 방출.
        enableEditing(doc) {
            const editRoot = mailDoc.selectEditRoot(doc, this.boundary)
            if (!editRoot) return
            this.editRoot = editRoot
            // 로드 시 원본 토큰을 보관(저장 게이트에서 손실 비교용).
            this.originalFlat = this.value
            // 변수/스니펫 자리를 알약으로 표시(속성 안 토큰은 건드리지 않음).
            mailDoc.flatToChips(editRoot, doc, this.values)
            mailDoc.applyEditability(doc, editRoot, this.boundary)
            // 서식이 class 가 아니라 요소 inline style 로 나오게(Gmail 등에서 안전).
            try { doc.execCommand('styleWithCSS', false, true) } catch (e) { /* 구형 무시 */ }

            // 가드 1: 선택이 편집 영역 밖으로 나가면 안으로 되돌린다.
            this.on(doc, 'selectionchange', () => this.clampSelection())
            // 가드 2: 삭제·입력 범위가 편집 영역을 벗어나면 막는다.
            this.on(editRoot, 'beforeinput', (e) => this.guardBeforeInput(e))
            // 가드 3: 붙여넣기는 이메일 안전 화이트리스트로 정제 후 삽입(백엔드 무살균).
            this.on(editRoot, 'paste', (e) => this.onPaste(e))
            // 입력/포커스 이탈 시 저장용 HTML 방출(디바운스).
            this.on(editRoot, 'input', () => this.scheduleEmit())
            this.on(editRoot, 'blur', () => { if (this.emitter.pending()) this.emitChange() })
        },

        // 툴바 command 를 iframe 문서에 실행(execCommand). selection 은 mousedown
        // preventDefault 로 이미 유지돼 있다.
        onCommand({ command, value }) {
            if (!this.doc || !this.editRoot) return
            // 명령 대상이 편집 영역 안일 때만(프레임 오염 방지).
            const win = this.doc.defaultView
            const sel = win && win.getSelection ? win.getSelection() : null
            if (sel && sel.rangeCount &&
                !mailDoc.isNodeWithin(sel.getRangeAt(0).startContainer, this.editRoot)) {
                return
            }
            try {
                this.doc.execCommand(command, false, value == null ? undefined : value)
            } catch (e) { /* 미지원 무시 */ }
            this.scheduleEmit()
        },

        // 붙여넣기 정제: text/html 을 화이트리스트로 자르고, HTML 없으면 plain text.
        onPaste(e) {
            const cd = e.clipboardData || (this.doc.defaultView && this.doc.defaultView.clipboardData)
            if (!cd) return
            e.preventDefault()
            const html = cd.getData('text/html')
            const win = this.doc.defaultView
            let insert
            if (html) {
                if (!this.purifier) this.purifier = createDOMPurify(win)
                insert = mailSanitize.sanitizePastedHtml(this.purifier, html)
            } else {
                const text = cd.getData('text/plain') || ''
                insert = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                    .replace(/\n/g, '<br>')
            }
            try {
                this.doc.execCommand('insertHTML', false, insert)
            } catch (err) { /* noop */ }
            this.scheduleEmit()
        },

        // editRoot(및 하위)만 유효 선택으로 간주. 밖이면 selection 을 편집 영역으로 clamp.
        clampSelection() {
            if (this.clamping || !this.doc || !this.editRoot) return
            const win = this.doc.defaultView
            const sel = win && win.getSelection ? win.getSelection() : null
            if (!sel || sel.rangeCount === 0) return
            const a = sel.anchorNode
            const f = sel.focusNode
            const inA = mailDoc.isNodeWithin(a, this.editRoot)
            const inF = mailDoc.isNodeWithin(f, this.editRoot)
            if (inA && inF) return
            // 하나라도 밖이면 caret 을 editRoot 안으로 접는다.
            this.clamping = true
            try {
                const range = this.doc.createRange()
                range.selectNodeContents(this.editRoot)
                range.collapse(false) // 끝으로
                sel.removeAllRanges()
                sel.addRange(range)
            } catch (e) { /* noop */ } finally {
                this.clamping = false
            }
        },

        // 삭제/입력이 편집 영역 경계를 넘으면 막는다(프레임 훼손 방지).
        guardBeforeInput(e) {
            if (!this.editRoot) return
            const win = this.doc && this.doc.defaultView
            const sel = win && win.getSelection ? win.getSelection() : null
            if (!sel || sel.rangeCount === 0) return
            const r = sel.getRangeAt(0)
            const inStart = mailDoc.isNodeWithin(r.startContainer, this.editRoot)
            const inEnd = mailDoc.isNodeWithin(r.endContainer, this.editRoot)
            if (!inStart || !inEnd) {
                e.preventDefault()
            }
        },

        scheduleEmit() {
            this.emitter.schedule()
        },

        // 현재 편집 상태를 저장용 flat HTML 로 직렬화해 부모로 올린다.
        // 진입 시 대기 중인 디바운스 타이머를 취소한다 — blur 로 즉시 emit 한 뒤 남은 타이머가
        // 이전 HTML 을 재방출해 부모(소스 textarea)의 최신 편집을 덮어쓰는 경합 방지.
        emitChange() {
            this.emitter.cancel()
            if (!this.doc) return
            const bodyText = mailDoc.serializeDoc(this.doc, this.boundary, this.doctype)
            this.$emit('input', bodyText)
        },

        // 현재 저장용 HTML 을 즉시 반환(부모가 저장 시 당겨쓰는 용도).
        // 저장 직전 프레임 버튼/카드의 class 스타일을 요소 inline 으로 박아(getComputedStyle
        // = 실렌더 기준) Gmail 등이 <style> 를 지워도 버튼이 유지되게 한다.
        getBodyText() {
            if (!this.doc) return this.value
            const win = this.doc.defaultView
            if (win) mailDoc.inlineFrameClasses(this.doc, win)
            return mailDoc.serializeDoc(this.doc, this.boundary, this.doctype)
        },

        // 저장 게이트: 현재 저장본이 로드 시점 토큰을 하나도 잃지 않았는지 검사.
        // 부모가 저장 직전 호출 → false 면 저장 차단(변수 손상 = 발송 시 이름 누락 방지).
        // 반환: { ok, bodyText, lost }
        validateForSave() {
            const bodyText = this.getBodyText()
            const ok = mailDoc.tokensPreserved(this.originalFlat, bodyText)
            return { ok, bodyText }
        },

        // 변수 삽입(부모의 insert 이벤트 → 현재 커서 위치에 알약 삽입).
        // token = 저장될 원문('{{ reserve user.e_mail }}'), label = 값 맵 키/표시 폴백('user.e_mail').
        insertVariable(token, label) {
            if (!this.doc || !this.editRoot) return
            const chip = mailDoc.makeChip(this.doc, token, label, 'var', this.values)
            const win = this.doc.defaultView
            const sel = win && win.getSelection ? win.getSelection() : null
            // 커서가 편집 영역 안이면 그 자리에, 아니면 편집 영역 끝에 삽입.
            let range = null
            if (sel && sel.rangeCount &&
                mailDoc.isNodeWithin(sel.getRangeAt(0).startContainer, this.editRoot)) {
                range = sel.getRangeAt(0)
            } else {
                range = this.doc.createRange()
                range.selectNodeContents(this.editRoot)
                range.collapse(false)
            }
            range.deleteContents()
            range.insertNode(chip)
            // 알약 뒤로 커서 이동
            range.setStartAfter(chip)
            range.collapse(true)
            if (sel) { sel.removeAllRanges(); sel.addRange(range) }
            this.scheduleEmit()
        },

        // 이벤트 리스너 등록 + 정리 목록 기록.
        on(target, type, fn) {
            target.addEventListener(type, fn)
            this.listeners.push({ target, type, fn })
        },

        teardown() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
                this.resizeObserver = null
            }
            this.emitter.cancel()
            this.heightFrames.forEach(({ win, id }) => {
                try {
                    if (win && win.cancelAnimationFrame) {
                        win.cancelAnimationFrame(id)
                    }
                } catch (e) { /* noop */ }
            })
            this.heightFrames = []
            this.heightTimers.forEach(({ win, id }) => {
                try {
                    if (win) win.clearTimeout(id)
                } catch (e) { /* noop */ }
            })
            this.heightTimers = []
            this.listeners.forEach(({ target, type, fn }) => {
                try { target.removeEventListener(type, fn) } catch (e) { /* noop */ }
            })
            this.listeners = []
        },

        // 칩/편집용 CSS 를 iframe 문서에 직접 주입(scoped 스타일은 iframe 경계를 못 넘는다).
        injectCanvasStyle(doc) {
            const style = doc.createElement('style')
            style.setAttribute('data-mail-canvas', '')
            style.textContent = [
                '.mail-var-chip{display:inline-block;padding:0 4px;margin:0 1px;',
                'border-radius:4px;background:#eef2ff;border:1px solid #c7d2fe;',
                'color:#3730a3;font-size:.92em;white-space:nowrap;user-select:none;cursor:default}',
                '.mail-snippet-error{display:inline-block;padding:2px 4px;margin:1px;',
                'border-radius:4px;border:1px solid #dc3545;color:#dc3545;',
                'background:#fff5f5;font:12px/1.4 sans-serif}',
            ].join('')
            const host = doc.head || doc.body || doc.documentElement
            if (host) host.appendChild(style)
        },

        // iframe 은 콘텐츠 높이를 스스로 못 늘리므로 documentElement.scrollHeight 를 수동 동기화.
        // 이미지 등 콘텐츠는 doc.write 직후가 아니라 나중에 렌더되므로 즉시 1회로는 부족하다.
        // → rAF·load·지연 안전망으로 여러 번 맞추고, ResizeObserver 로 이후 변화도 계속 반영한다.
        bindAutoHeight(iframe, doc) {
            const sync = () => {
                // documentElement.scrollHeight 는 body margin 까지 포함하므로
                // iframe 을 이 높이에 맞추면 iframe 내부에 자체 스크롤이 생기지 않는다.
                const de = doc.documentElement
                const h = Math.max(
                    de ? de.scrollHeight : 0,
                    doc.body ? doc.body.scrollHeight : 0,
                )
                if (h) iframe.style.height = h + 'px'
            }
            sync()
            const win = doc.defaultView
            if (win) {
                const frameId = win.requestAnimationFrame(sync)
                this.heightFrames.push({ win, id: frameId })
                // 이미지 등 리소스가 늦게 로드돼 높이가 커지는 경우까지 반영.
                this.on(win, 'load', sync)
                // 초기 렌더 안정화 안전망(리소스 캐시 여부와 무관하게 수렴).
                ;[120, 400, 900].forEach(ms => {
                    const timerId = win.setTimeout(sync, ms)
                    this.heightTimers.push({ win, id: timerId })
                })
            }
            if (typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(sync)
                // documentElement 를 관찰해야 body margin·이미지 로드로 인한 높이 변화를 놓치지 않는다.
                if (doc.documentElement) this.resizeObserver.observe(doc.documentElement)
                if (doc.body) this.resizeObserver.observe(doc.body)
            }
        },
    },
}
</script>

<style scoped>
.mail-doc-canvas {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #fff;
}
.mail-doc-canvas__frame {
    width: 100%;
    min-height: 320px;
    border: 0;
    display: block;
}
</style>
